import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ExcludedDateDetails from "./ExcludedDateDetails";
import ExcludedDatesList from "./ExcludedDatesList";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectExcludedDates } from "../../../store/robot-configurations/robot-configurations-selectors";
import { selectRooms } from "../../../store/room/room-selectors";
import ExcludedDate from "../../../interfaces/robot/excluded-dates/excluded-date";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import CustomSplitColumnCard from "../../misc/CustomSplitColumnCard";
import Stack from "@mui/material/Stack";
import CustomAlert from "../../Feedback/CustomAlert";

const ExcludedDates: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getExcludedDates, patchExcludedDatesRestSet } = useSharedActions();

  const excludedDates = useSharedSelector((state) => selectExcludedDates(state));
  const availableRooms = useSharedSelector((state) => selectRooms(state));

  const [currentlyEditedExcludedDate, setCurrentlyEditedExcludedDate] = useState<{
    excludedDate: ExcludedDate | null;
    isNew: boolean;
  } | null>(null);

  useEffect(() => {
    if (excludedDates.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getExcludedDates());
    }
  }, [dispatch, getExcludedDates, excludedDates.rest.getStatus]);

  const addExcludedDateClickHandler = (_event: React.MouseEvent) => {
    setCurrentlyEditedExcludedDate({ excludedDate: null, isNew: true });
  };

  const handleEditDate = (date: ExcludedDate) => {
    setCurrentlyEditedExcludedDate({
      excludedDate: date,
      isNew: false,
    });
  };

  const excludedDateDetailsCloseHandler = () => {
    setCurrentlyEditedExcludedDate(null);
  };

  const handleExcludedDatesSavingSnackbarClose = () => {
    dispatch(
      patchExcludedDatesRestSet({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      })
    );
  };

  return (
    <React.Fragment>
      <Stack p={0.3}>
        <CustomSplitColumnCard
          title={t("configuration-excludedDates-cardtitle")}
          content={[
            {
              left: (
                <Stack pr={2}>
                  <CustomAlert severity="info" header={t("info-excluded-dates")} />
                </Stack>
              ),
              right: (
                <ExcludedDatesList
                  availableRooms={availableRooms}
                  onAddExcludedDate={addExcludedDateClickHandler}
                  onEditDate={handleEditDate}
                />
              ),
            },
          ]}
        />
      </Stack>

      {currentlyEditedExcludedDate != null && (
        <ExcludedDateDetails
          editedDate={currentlyEditedExcludedDate.excludedDate}
          onDialogClose={excludedDateDetailsCloseHandler}
          availableRooms={availableRooms}
        />
      )}

      <CustomSnackbar
        title={t("error-saving-excludedDates")}
        open={
          excludedDates.rest.postStatus === HttpFetchStatus.Failed ||
          excludedDates.rest.patchStatus === HttpFetchStatus.Failed ||
          excludedDates.rest.deleteStatus === HttpFetchStatus.Failed
        }
        onClose={handleExcludedDatesSavingSnackbarClose}
        textMessages={getSaveErrorMessages(excludedDates.rest.postError ?? excludedDates.rest.patchError)}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-excludedDates")}
        severity="success"
        open={
          excludedDates.rest.postStatus === HttpFetchStatus.Success ||
          excludedDates.rest.patchStatus === HttpFetchStatus.Success ||
          excludedDates.rest.deleteStatus === HttpFetchStatus.Success
        }
        onClose={handleExcludedDatesSavingSnackbarClose}
      />
    </React.Fragment>
  );
};
export default ExcludedDates;
